// @ts-nocheck
import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Sparkles,
  RefreshCw,
  AlertCircle,
  TrendingUp,
  ListChecks,
  FileText,
  LineChart,
  AlertTriangle,
  ClipboardList,
  ArrowLeft,
  Share2,
} from 'lucide-react';
import ShareModal from './components/ShareModal';
import useWorkbench from './hooks/useWorkbench';
import ExecutiveOverview from './sections/ExecutiveOverview';
import EbitdaWaterfall from './sections/EbitdaWaterfall';
import AdjustmentsWorkbench from './sections/AdjustmentsWorkbench';
import AdjustedIncomeStatement from './sections/AdjustedIncomeStatement';
import AccountDrilldown from './sections/AccountDrilldown';
import TrendDiagnostics from './sections/TrendDiagnostics';
import ConcentrationRisk from './sections/ConcentrationRisk';
import PunchList from './sections/PunchList';
import QoEWorkbenchLanding from './sections/QoEWorkbenchLanding';
import './QoEWorkbench.css';

const SECTIONS = [
  { id: 'overview', label: 'Overview', icon: Sparkles },
  { id: 'waterfall', label: 'EBITDA Waterfall', icon: TrendingUp },
  { id: 'workbench', label: 'Adjustments Workbench', icon: ListChecks },
  { id: 'income-statement', label: 'Income Statement', icon: FileText },
  { id: 'trend', label: 'Trend Diagnostics', icon: LineChart },
  { id: 'concentration', label: 'Concentration Risk', icon: AlertTriangle },
  { id: 'punch-list', label: 'Punch List', icon: ClipboardList },
];

const QoEWorkbench = ({
  mappingSessionId,
  sessionName,
  mapperSessions = [],
  mapperSessionsLoading = false,
  onSelectSession,
  onClearSession,
}) => {
  const overviewRef = useRef(null);
  const waterfallRef = useRef(null);
  const workbenchRef = useRef(null);
  const incomeStatementRef = useRef(null);
  const trendRef = useRef(null);
  const concentrationRef = useRef(null);
  const punchListRef = useRef(null);

  const refsById = useMemo(
    () => ({
      overview: overviewRef,
      waterfall: waterfallRef,
      workbench: workbenchRef,
      'income-statement': incomeStatementRef,
      trend: trendRef,
      concentration: concentrationRef,
      'punch-list': punchListRef,
    }),
    []
  );

  const [drilldownAccount, setDrilldownAccount] = useState(null);
  const openDrilldown = useCallback((accountName) => setDrilldownAccount(accountName), []);
  const closeDrilldown = useCallback(() => setDrilldownAccount(null), []);

  const [shareOpen, setShareOpen] = useState(false);

  const scrollTo = (id) => {
    const ref = refsById[id];
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const { bootstrap, loading, error, mutate, reseed, savingCount } = useWorkbench(mappingSessionId);

  if (!mappingSessionId) {
    return (
      <QoEWorkbenchLanding
        sessions={mapperSessions}
        loading={mapperSessionsLoading}
        onSelect={onSelectSession}
      />
    );
  }

  if (loading && !bootstrap) {
    return (
      <div className="qoe-wb-loading">
        <div className="spinner"></div>
        <p>Loading workbench…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="qoe-wb-error">
        <AlertCircle size={28} />
        <h2>Could not load workbench</h2>
        <p>{error}</p>
        <button className="qoe-wb-btn" onClick={reseed}>
          <RefreshCw size={16} /> Retry seed from latest flagging
        </button>
      </div>
    );
  }

  if (!bootstrap) return null;

  return (
    <div className="qoe-wb">
      <header className="qoe-wb-header">
        <div>
          <div className="qoe-wb-eyebrow">QoE Workbench</div>
          <h1 className="qoe-wb-title">{sessionName || bootstrap.name || 'Engagement'}</h1>
        </div>
        <div className="qoe-wb-header-actions">
          {onClearSession && (
            <button
              className="qoe-wb-btn ghost qoe-wb-tactile"
              onClick={onClearSession}
              title="Switch to a different saved engagement"
            >
              <ArrowLeft size={14} /> Switch engagement
            </button>
          )}
          <button
            className="qoe-wb-btn ghost qoe-wb-tactile"
            onClick={() => setShareOpen(true)}
            title="Share this engagement with teammates in your tenant"
          >
            <Share2 size={14} /> Share
          </button>
          <button
            className="qoe-wb-btn ghost qoe-wb-tactile"
            onClick={reseed}
            title="Re-seed from the latest qoe_report — overwrites all analyst review state"
          >
            <RefreshCw size={14} /> Re-seed from flagging
          </button>
        </div>
      </header>

      <nav className="qoe-wb-nav">
        {SECTIONS.map(({ id, label, icon: Icon }) => (
          <button key={id} className="qoe-wb-nav-link" onClick={() => scrollTo(id)}>
            <Icon size={14} />
            <span>{label}</span>
          </button>
        ))}
      </nav>

      <main className="qoe-wb-main">
        <section ref={overviewRef} id="overview">
          <ExecutiveOverview bootstrap={bootstrap} />
        </section>

        <section ref={waterfallRef} id="waterfall">
          <EbitdaWaterfall bootstrap={bootstrap} />
        </section>

        <section ref={workbenchRef} id="workbench">
          <AdjustmentsWorkbench
            bootstrap={bootstrap}
            mutate={mutate}
            savingCount={savingCount}
            onOpenDrilldown={openDrilldown}
          />
        </section>

        <section ref={incomeStatementRef} id="income-statement">
          <AdjustedIncomeStatement
            mappingSessionId={mappingSessionId}
            bootstrap={bootstrap}
            onOpenDrilldown={openDrilldown}
          />
        </section>

        <section ref={trendRef} id="trend">
          <TrendDiagnostics mappingSessionId={mappingSessionId} />
        </section>

        <section ref={concentrationRef} id="concentration">
          <ConcentrationRisk
            mappingSessionId={mappingSessionId}
            bootstrap={bootstrap}
            onOpenDrilldown={openDrilldown}
          />
        </section>

        <section ref={punchListRef} id="punch-list">
          <PunchList
            mappingSessionId={mappingSessionId}
            sessionName={sessionName || bootstrap.name}
            bootstrap={bootstrap}
            onOpenDrilldown={openDrilldown}
          />
        </section>
      </main>

      {drilldownAccount && (
        <AccountDrilldown
          mappingSessionId={mappingSessionId}
          accountName={drilldownAccount}
          onClose={closeDrilldown}
        />
      )}

      {shareOpen && (
        <ShareModal
          sessionId={mappingSessionId}
          sessionName={sessionName || bootstrap.name}
          onClose={() => setShareOpen(false)}
        />
      )}
    </div>
  );
};

export default QoEWorkbench;
